import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { marketingAPIService, MarketingCampaign, CampaignAnalytics, MarketingContact } from '../services/api'
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  BarChart3,
  Send,
  Users,
  Mail,
  TrendingUp,
  X,
  Eye,
  UserPlus,
  Search
} from 'lucide-react'

const campaignSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  campaignType: z.enum(['drip', 'one-time', 'triggered', 'nurturing', 're-engagement', 'win-back', 'abandonment', 'welcome']),
  templateId: z.string().optional(),
  templateName: z.string().optional(), // Keep for backward compatibility
  triggerEvent: z.string().optional(),
  isActive: z.boolean().default(true),
})

type CampaignFormData = z.infer<typeof campaignSchema>

const MarketingCampaigns: React.FC = () => {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<MarketingCampaign | null>(null)
  const [viewingAnalytics, setViewingAnalytics] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [enrollingCampaign, setEnrollingCampaign] = useState<MarketingCampaign | null>(null)
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])
  const [contactSearchQuery, setContactSearchQuery] = useState('') // Add search state

  const { data: account } = useQuery('marketing-account', () =>
    marketingAPIService.getAccount()
  )

  const { data: campaigns, isLoading } = useQuery('marketing-campaigns', () =>
    marketingAPIService.getCampaigns()
  )

  // Fetch templates from database (system templates)
  const { data: systemTemplates } = useQuery(
    'marketing-system-templates',
    () => marketingAPIService.getBusinessTemplates('system'),
    { enabled: true }
  )

  const { data: contactsData } = useQuery(
    'marketing-contacts-for-enroll',
    () => marketingAPIService.getContacts({ unsubscribed: false, limit: 1000 }),
    { enabled: !!enrollingCampaign }
  )

  const { data: enrollmentsData } = useQuery(
    ['campaign-enrollments', enrollingCampaign?.id],
    () => marketingAPIService.getCampaignEnrollments(enrollingCampaign!.id, { status: 'active,paused' }),
    { enabled: !!enrollingCampaign }
  )

  // Filter out already enrolled contacts (only for THIS campaign) and apply search
  const availableContacts = React.useMemo(() => {
    if (!contactsData?.contacts) {
      return []
    }
    
    // Only filter by enrollments if we have enrollment data for the current campaign
    // This ensures we only exclude contacts enrolled in THIS specific campaign, not other campaigns
    let filtered = contactsData.contacts
    
    if (enrollmentsData?.enrollments && enrollingCampaign?.id) {
      // Double-check that enrollments are for the current campaign (safety check)
      const currentCampaignEnrollments = enrollmentsData.enrollments.filter(
        (e: any) => e.campaignId === enrollingCampaign.id
      )
      const enrolledContactIds = new Set(currentCampaignEnrollments.map((e: any) => e.contactId))
      filtered = contactsData.contacts.filter(contact => !enrolledContactIds.has(contact.id))
    }
    
    // Apply search filter
    if (contactSearchQuery.trim()) {
      const query = contactSearchQuery.toLowerCase()
      filtered = filtered.filter(contact => 
        contact.email.toLowerCase().includes(query) ||
        contact.firstName?.toLowerCase().includes(query) ||
        contact.lastName?.toLowerCase().includes(query) ||
        contact.companyName?.toLowerCase().includes(query) ||
        contact.tags?.some(tag => tag.toLowerCase().includes(query))
      )
    }
    
    return filtered
  }, [contactsData, enrollmentsData, contactSearchQuery, enrollingCampaign?.id])

  const { data: analyticsData } = useQuery(
    ['campaign-analytics', viewingAnalytics],
    () => marketingAPIService.getCampaignAnalytics(viewingAnalytics!),
    { enabled: !!viewingAnalytics }
  )

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
  })

  const watchedTemplateId = watch('templateId')

  // Load template when selected (but not when editing a campaign)
  React.useEffect(() => {
    if (watchedTemplateId && watchedTemplateId !== selectedTemplate && !editingCampaign) {
      setSelectedTemplate(watchedTemplateId)
      loadTemplate(watchedTemplateId)
    }
  }, [watchedTemplateId, editingCampaign])

  const loadTemplate = async (templateId: string) => {
    try {
      // Find template in system templates
      const template = systemTemplates?.templates?.find((t: any) => t.id === templateId)
      if (template) {
        // Only auto-fill if creating a new campaign
        if (!editingCampaign) {
          setValue('name', template.name)
          setValue('description', template.description || '')
          setValue('campaignType', template.campaignType)
        }
      }
    } catch (error) {
      console.error('Failed to load template:', error)
    }
  }

  const createMutation = useMutation(
    (data: any) => marketingAPIService.createCampaign(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-campaigns')
        setShowCreateModal(false)
        reset()
        toast.success('Campaign created successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create campaign')
      },
    }
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      marketingAPIService.updateCampaign(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-campaigns')
        setEditingCampaign(null)
        setShowCreateModal(false)
        reset()
        toast.success('Campaign updated successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update campaign')
      },
    }
  )

  const deleteMutation = useMutation(
    (id: string) => marketingAPIService.deleteCampaign(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketing-campaigns')
        toast.success('Campaign deleted successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete campaign')
      },
    }
  )

  const enrollMutation = useMutation(
    ({ campaignId, contactIds }: { campaignId: string; contactIds: string[] }) =>
      marketingAPIService.enrollContacts(campaignId, contactIds),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('marketing-campaigns')
        setEnrollingCampaign(null)
        setSelectedContacts([])
        setContactSearchQuery('') // Clear search on success
        toast.success(`Successfully enrolled ${data.enrolled.length} contacts!`)
        if (data.skipped.length > 0) {
          toast(`Skipped ${data.skipped.length} contacts (already enrolled or unsubscribed)`, { icon: 'ℹ️' })
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to enroll contacts')
      },
    }
  )

  const onSubmit = async (data: CampaignFormData) => {
    try {
      if (editingCampaign) {
        // When editing, only update the fields that changed
        // Preserve existing steps unless a new template is selected
        let updateData: any = {
          name: data.name,
          description: data.description,
          campaignType: data.campaignType,
          templateId: data.templateId,
          templateName: data.templateName,
          triggerEvent: data.triggerEvent || null,
          isActive: data.isActive
        }

        // Only load new steps if template was changed
        if (data.templateId) {
          const template = systemTemplates?.templates?.find((t: any) => t.id === data.templateId)
          if (template && template.steps) {
            updateData.steps = template.steps
          }
        }

        await updateMutation.mutateAsync({
          id: editingCampaign.id,
          data: updateData,
        })
      } else {
        // When creating, load template steps if template is selected
        let steps: any[] = []
        if (data.templateId) {
          const template = systemTemplates?.templates?.find((t: any) => t.id === data.templateId)
          if (template && template.steps) {
            steps = template.steps
          }
        }
        await createMutation.mutateAsync({ 
          ...data, 
          steps,
          templateId: data.templateId,
          templateName: data.templateId ? systemTemplates?.templates?.find((t: any) => t.id === data.templateId)?.templateName : data.templateName
        })
      }
    } catch (error) {
      // Error handled by mutation
    }
  }

  const toggleCampaignStatus = (campaign: MarketingCampaign) => {
    updateMutation.mutate({
      id: campaign.id,
      data: { isActive: !campaign.isActive },
    })
  }

  const deleteCampaign = (id: string) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleEnrollContacts = () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select at least one contact')
      return
    }
    if (!enrollingCampaign) return

    enrollMutation.mutate({
      campaignId: enrollingCampaign.id,
      contactIds: selectedContacts
    })
  }

  const toggleContactSelection = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    )
  }

  const selectAllContacts = () => {
    if (!availableContacts) return
    setSelectedContacts(availableContacts.map(c => c.id))
  }

  const deselectAllContacts = () => {
    setSelectedContacts([])
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketing Campaigns</h1>
        <p className="text-gray-600">Create and manage email marketing campaigns</p>
      </div>

      {/* Account Stats */}
      {account && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Email Quota</p>
                <p className="text-2xl font-bold text-gray-900">
                  {account.account.emailsSent.toLocaleString()} / {account.account.emailQuota.toLocaleString()}
                </p>
              </div>
              <Mail className="h-10 w-10 text-blue-500" />
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${(account.account.emailsSent / account.account.emailQuota) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-bold text-gray-900">
                  {campaigns?.campaigns?.length || 0}
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Subscription Tier</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">
                  {account.account.subscriptionTier || 'Free'}
                </p>
              </div>
              <Users className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Button */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={() => {
            setShowCreateModal(true)
            setEditingCampaign(null)
            reset()
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Create Campaign
        </button>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading campaigns...</p>
          </div>
        ) : campaigns?.campaigns?.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No campaigns yet. Create your first campaign to get started!</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enrollments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns?.campaigns?.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      {campaign.description && (
                        <div className="text-sm text-gray-500">{campaign.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                      {campaign.campaignType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-gray-400" />
                      {campaign.enrollmentsCount} ({campaign.activeEnrollmentsCount} active)
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>Open: {campaign.openRate?.toFixed(1) || 0}%</div>
                      <div>Click: {campaign.clickRate?.toFixed(1) || 0}%</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {campaign.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => {
                        setEnrollingCampaign(campaign)
                        setSelectedContacts([])
                      }}
                      className="text-purple-600 hover:text-purple-900"
                      title="Enroll Contacts"
                    >
                      <UserPlus className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setViewingAnalytics(campaign.id)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Analytics"
                    >
                      <BarChart3 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => toggleCampaignStatus(campaign)}
                      className={campaign.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                      title={campaign.isActive ? 'Pause Campaign' : 'Activate Campaign'}
                    >
                      {campaign.isActive ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingCampaign(campaign)
                        setShowCreateModal(true)
                        reset({
                          name: campaign.name,
                          description: campaign.description,
                          campaignType: campaign.campaignType,
                          templateId: (campaign as any).templateId || undefined,
                          templateName: campaign.templateName || undefined,
                          triggerEvent: (campaign as any).triggerEvent || undefined,
                          isActive: campaign.isActive,
                        })
                      }}
                      className="text-gray-600 hover:text-gray-900"
                      title="Edit Campaign"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => deleteCampaign(campaign.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Campaign"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setShowCreateModal(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Name
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Welcome Series"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Automated welcome series for new signups"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Type
                  </label>
                  <select
                    {...register('campaignType')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="drip">Drip (Multi-email series)</option>
                    <option value="one-time">One-time (Single email)</option>
                    <option value="triggered">Triggered (Event-based)</option>
                    <option value="nurturing">Nurturing (Long-term relationship building)</option>
                    <option value="re-engagement">Re-Engagement (Re-engage inactive contacts)</option>
                    <option value="win-back">Win-Back (Win back lost customers)</option>
                    <option value="abandonment">Abandonment (Cart/service abandonment)</option>
                    <option value="welcome">Welcome (Welcome series for new customers)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template (Optional)
                  </label>
                  <select
                    {...register('templateId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a template...</option>
                    {systemTemplates?.templates?.map((template: any) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.campaignType ? `(${template.campaignType})` : ''}
                      </option>
                    ))}
                  </select>
                  {systemTemplates?.templates?.length === 0 && (
                    <p className="mt-1 text-sm text-gray-500">
                      No templates available. Create templates in the Templates page.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auto-Enroll Trigger (Optional)
                  </label>
                  <select
                    {...register('triggerEvent')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No auto-enrollment</option>
                    <option value="business_signup">Business Signup (New business registers)</option>
                    <option value="customer_created">Customer Created (New customer added)</option>
                    <option value="subscription_cancelled">Subscription Cancelled</option>
                    <option value="subscription_trial_ending">Trial Ending Soon</option>
                    <option value="contact_inactive">Contact Inactive</option>
                    <option value="service_completed">Service Completed</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    When selected, contacts will automatically be enrolled when this event occurs
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('isActive')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Campaign is active
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading
                      ? 'Saving...'
                      : editingCampaign
                      ? 'Update Campaign'
                      : 'Create Campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {viewingAnalytics && analyticsData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setViewingAnalytics(null)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Campaign Analytics</h2>
                <button
                  onClick={() => setViewingAnalytics(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Emails Sent</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.analytics.emailsSent}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Delivered</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.analytics.delivered}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Opened</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.analytics.opened}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Clicked</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.analytics.clicked}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Open Rate</p>
                  <p className="text-3xl font-bold text-green-600">{analyticsData.analytics.openRate}%</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Click Rate</p>
                  <p className="text-3xl font-bold text-blue-600">{analyticsData.analytics.clickRate}%</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Bounce Rate</p>
                  <p className="text-3xl font-bold text-red-600">{analyticsData.analytics.bounceRate}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Contacts Modal */}
      {enrollingCampaign && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setEnrollingCampaign(null)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Enroll Contacts</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Campaign: <span className="font-semibold">{enrollingCampaign.name}</span>
                  </p>
                </div>
                <button
                  onClick={() => setEnrollingCampaign(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search contacts by name, email, company, or tags..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  {contactSearchQuery && (
                    <button
                      onClick={() => setContactSearchQuery('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Selection Controls */}
              <div className="mb-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-4">
                  <button
                    onClick={selectAllContacts}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAllContacts}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                  >
                    Deselect All
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{selectedContacts.length}</span> selected
                </div>
              </div>

              {/* Contacts List */}
              <div className="space-y-2 mb-6">
                {availableContacts && availableContacts.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No contacts available to enroll</p>
                    <p className="text-sm text-gray-500 mt-2">
                      All contacts are either already enrolled or unsubscribed
                    </p>
                  </div>
                ) : (
                  availableContacts?.map((contact) => (
                    <label
                      key={contact.id}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedContacts.includes(contact.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => toggleContactSelection(contact.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">
                              {contact.firstName} {contact.lastName}
                            </div>
                            <div className="text-sm text-gray-600">{contact.email}</div>
                          </div>
                          {contact.companyName && (
                            <div className="text-sm text-gray-500">{contact.companyName}</div>
                          )}
                          {contact.subscriptionPlan && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              {contact.subscriptionPlan}
                            </span>
                          )}
                        </div>
                        {contact.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {contact.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setEnrollingCampaign(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnrollContacts}
                  disabled={selectedContacts.length === 0 || enrollMutation.isLoading}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {enrollMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Enrolling...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      Enroll {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingCampaigns

